<script setup lang="ts">
import { computed, onUnmounted, ref, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import { useQueryClient } from "@tanstack/vue-query";
import { useAppMode } from "@/composables/useAppMode";
import { useGameState } from "@/stores/gameState";
import { useUiState } from "@/stores/uiState";
import { TYPE } from "vue-toastification";
import { toast } from "@/helpers/toast";
import { copyToClipboard } from "@/helpers/copyToClipboard";
import UiButton from "@/components/ui/button/Button.vue";
import RoomMenu from "@/components/compete/RoomMenu.vue";

import PokerTable from "./components/table/PokerTable.vue";
import NLHSeat from "./components/table/NLHSeat.vue";

import CardsInHandActionsDesktop from "@/components/gameActions/CardsInHandDesktop.vue";
import CardsInHandActionsMobile from "@/components/gameActions/CardsInHandMobile.vue";

import PlayersPanel from "@/components/panels/PlayersPanel.vue";
import SessionLogPanel from "@/components/panels/SessionLog.vue";
import InviteFriends from "@/components/table/InviteFriends.vue";

import JoinGameModal from "@/components/modals/JoinGame.vue";
import EditAvatarModal from "@/components/modals/EditAvatar.vue";
import AddBotModal from "@/components/modals/AddBot.vue";

import PlayersSvg from "@/assets/icons/interface/players.svg?component";

import { closeTable } from "@/api/table/close";
import { createPlayerDefaultSitDown } from "@/api/assessment";

import type { SeatClickEvent } from "@acepoker/games-nlhe/table/types";
import AssessmentModeProgress from "@/components/ui/progressBars/AssessmentModeProgress.vue";

import {
  unmountChatUIWebComponent,
  cleanupAssistant,
} from "@/helpers/integrations";
import { useAssessmentPopupStore } from "@/stores/assessmentPopupState";
import { useAuth } from "@/stores/auth";
import { useGameplayInsightsStore } from "@/stores/gameplayInsightsState";
import { cn } from "@/lib/utils";
import { getCurrent } from "@/api/table/getCurrent";

import {
  useFlagsWithLocalStorage,
  type FlagValue,
} from "./helpers/featureToggles/useFlagsWithLocalStorage";
import { useMobileLandscape } from "@/composables/useMobileLandscape";

const router = useRouter();
const queryClient = useQueryClient();
const { t } = useI18n();
const { mode } = useAppMode();
const popupStore = useAssessmentPopupStore();
const gameplayInsightsStore = useGameplayInsightsStore();
const { isMobileLandscape } = useMobileLandscape();

// Feature flag implementation with loading and error states
const featureFlags = ref<Record<string, FlagValue>>({});
const flagsLoading = ref(true);
const flagsError = ref<Error | null>(null);
// Load feature flags on mount
onMounted(async () => {
  try {
    flagsLoading.value = true;
    featureFlags.value = await useFlagsWithLocalStorage([
      "aip-436_onboarding_v.2",
    ]);
    flagsLoading.value = false;
  } catch (error) {
    flagsError.value =
      error instanceof Error ? error : new Error(String(error));
    flagsLoading.value = false;
  }
});
// Helper to safely access flag values
const getFlag = (flagName: string): boolean => {
  if (flagsLoading.value || !featureFlags.value?.[flagName]) return false;
  return featureFlags.value[flagName].enabled === true;
};

const {
  roomState,
  roomId,
  player,
  isOwner,
  gameState,
  isMyTurn,
  lastAction,
  showButtons,
  gameMode,
  isSpectator,
  actionPending,
  winners,

  // TODO
  // isAtTable,
  // isAway,
  // isRequestSeat
  // isReadyToStart,
  isGameStarted,
} = storeToRefs(useGameState());
const {
  getPlayerById,
  getHoleCardsForPlayer,
  getSeatForPlayer,
  leaveTable,
  sitOut,
  standUp,
  clean,
  setSeatedTableId
} = useGameState();
const uiState = useUiState();
const { player: existingPlayer } = storeToRefs(useAuth());
const assessmentMode = computed(
  () => roomState.value.gameConfig?.assessmentMode,
);
const preConfiguredMode = computed(
  () => roomState.value.gameConfig?.preConfiguredMode,
);

const isShowdown = computed(() => !!winners.value.length)

const canAct = computed(() => {
  if (!player.value || roomState.value.hand == null) return false;

  const seat = getSeatForPlayer(player.value.playerId);
  const cards = player.value && getHoleCardsForPlayer(player.value.playerId);
  return seat && cards && seat.stack && cards.length > 0;
});


const isPredefinedMode = computed(() => {
  return roomState.value.gameConfig?.preConfiguredMode || roomState.value.gameConfig?.learningMode || roomState.value.gameConfig?.assessmentMode;
});

const handleSeatClick = async (seat: SeatClickEvent) => {
  if (actionPending.value) return

  if (isSpectator.value && seat.empty) {
    try {
      const data = await getCurrent()
      setSeatedTableId(data.table_id)
      uiState.modals.seatPlayerAlready.open()
      return
    } catch (err) {
      uiState.seatIndex = seat.index
      uiState.modals.joinGame.open()
      return
    }
  }

  if (isOwner.value) {
    if (seat.empty && !isSpectator.value) {
      uiState.seatIndex = seat.index;
      uiState.modals.addBot.open();
    }
  }
}

function handleSessionLog() {
  uiState.panels.sessionLog.open();
}

const linkToTable = computed(
  () => `${window.location.origin}/room/${roomId.value}`,
);

/**
 * Handles closing the table - owner action
 */
async function handleTableClose() {
  try {
    // Clean up first to stop any background play
    cleanChat();

    // Close the table on the server
    await closeTable(roomId.value!);
    queryClient.invalidateQueries({ queryKey: ["compete-my-tables"] });

    // Navigate away
    leave();
  } catch (error) {
    toast({
      message: t("Errors.label.Oops"),
      details: error instanceof Error ? error.message : String(error),
      icon: TYPE.ERROR,
    });
  }
}

function onAssessmentModeProgressUpdate(progress: number) {
  if (progress === 100) router.push({ name: "AssessmentModeCongratulations" });
}

/**
 * Handles the table leave action based on whether the player is seated at the table
 */
function handleTableLeave() {
  if (getFlag("aip-436_onboarding_v.2")) {
    if (!isPredefinedMode.value && !player.value?.states.includes('sit_out') && player.value?.states.includes('sit_down') && isGameStarted.value) {
      standUp();
      uiState.modals.attention.open({
        titleKey: 'TableLeaveAttentionModal.label.Title',
        descriptionKey: 'TableLeaveAttentionModal.label.Description',
        buttonKey: 'TableLeaveAttentionModal.button.Okay'
      })
    } else if (existingPlayer?.value?.email) {
      cleanChat();
      leaveTable();
      gameplayInsightsStore.show({
        playerId: existingPlayer.value.player_id || "",
        playerEmail: existingPlayer.value.email || "",
      });
    } else {
      cleanChat();
      leaveTable();
      showAnalysisPopup();
    }
  } else {
    cleanChat();
    leave();
  }
}

/**
 * Shows the analysis popup with player details
 */
function showAnalysisPopup() {
  standUp();
  popupStore.show({
    playerId: existingPlayer?.value?.player_id || "",
    playerEmail: existingPlayer?.value?.email || "",
    url: "story",
  });
}

/**
 * Navigate away from the room
 */
const leave = () => {
  if (getFlag("aip-436_onboarding_v.2")) {
    router.push({ name: "home2" });
    return;
  }

  const destination =
    window.history.state?.back === "/table/create"
      ? { name: "CompeteMyTables" }
      : window.history.state?.back
        ? window.history.state.back
        : { name: "CompeteLobby" };

  router.push(destination);
};

function copyTableLink() {
  copyToClipboard(linkToTable.value);
  toast({
    message: t("Compete.message.LinkCopied"),
    icon: TYPE.SUCCESS,
  });
}
function handleSitOut() {
  if (isSpectator.value) return;
  sitOut();
  uiState.modals.attention.open({
    titleKey: 'TableSitOutAttentionModal.label.Title',
    descriptionKey: 'TableSitOutAttentionModal.label.Description',
    buttonKey: 'TableSitOutAttentionModal.button.Okay'
  })
}
function handleStandUp() {
  if (isSpectator.value) return;
  standUp();
  if (isShowdown.value) return;
  uiState.modals.attention.open({
    titleKey: 'TableStandUpAttentionModal.label.Title',
    descriptionKey: 'TableStandUpAttentionModal.label.Description',
    buttonKey: 'TableStandUpAttentionModal.button.Okay'
  })
}

function cleanChat() {
  cleanupAssistant();
  unmountChatUIWebComponent();
}

onUnmounted(() => {
  leaveTable();
  clean();
  cleanChat();
  uiState.closeAllPanels();
  // Close modals on unmount
  popupStore.close();
  gameplayInsightsStore.close();
});

watch(
  [preConfiguredMode, assessmentMode],
  () => {
    if (roomId.value && (preConfiguredMode.value || assessmentMode.value)) {
      createPlayerDefaultSitDown(roomId.value);
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="compete-room relative flex h-full w-full flex-col items-center justify-start overflow-hidden pt-[10vh]"
    :class="{
      'compete-room--desktop': mode === 'desktop',
      'compete-room--mobile': mode === 'mobile',
    }">
    <!-- header part -->
    <header :class="cn(
      'fixed left-0 right-0 top-4 z-50 flex justify-between gap-2',
      assessmentMode ? 'top-0 gap-0 px-0' : 'top-4 gap-2 px-4',
    )
      ">
      <div>
        <room-menu v-if="!assessmentMode" :isOwner="isOwner" :isSpectator="isSpectator" :playerStates="player?.states"
          :preConfiguredMode="preConfiguredMode" @copy-link="copyTableLink" @leave-table="handleTableLeave"
          @close-table="handleTableClose" @sit-out="handleSitOut" @stand-up="handleStandUp"
          @session-log="handleSessionLog" />
      </div>
      <assessment-mode-progress v-if="assessmentMode" :label="$t('Compete.label.Completion')"
        container-class-name="w-full md:w-[531px] rounded-none md:rounded-xl"
        :handsCurrentlyPlayed="roomState.handNumber" with-notifications :buttonText="$t('Compete.button.FinishLater')"
        @progressUpdate="onAssessmentModeProgressUpdate" />

      <div>
        <ui-button v-if="!assessmentMode && !preConfiguredMode" id="players-button" size="icon"
          class="bg-menuItemBg h-9 w-9 rounded-full"
          @click="uiState.panels.playersPanel.open()"><players-svg /></ui-button>
      </div>
    </header>

    <poker-table class="compete-room__table">
      <!-- TODO: When it should be shown? -->
      <!-- <template #seat-popup="{ index, empty }">
          <seat-request-panel v-if="empty" :seat-index="index" />
        </template> -->

      <template #addon-bottom v-if="!isGameStarted">
        <invite-friends :link="linkToTable" />
      </template>

      <template #table-seat="seatProps">
        <NLHSeat v-bind="seatProps" :disable="actionPending" :settings="{ enableTimer: true }"
          @seat-click="handleSeatClick" />
      </template>
    </poker-table>

    <!-- bottom part -->

    <transition appear name="room__animation-bottom-slide">
      <!-- <seat-request-game-actions class="room__game-actions" v-if="isRequestingSeat" /> -->
      <!-- <now-away-actions class="room__game-actions" v-else-if="isAway"></now-away-actions> -->
      <!-- <ready-to-start-game-actions
          class="room__game-actions"
          v-else-if="gameState === GameState.PREGAME && isReadyToStart && isOwner"
        /> -->
      <cards-in-hand-actions-desktop v-if="mode === 'desktop'" :can-act="canAct" :is-showdown="isShowdown" :class="{
        'room__game-actions': true,
        'room__game-actions--landscape': isMobileLandscape,
      }" />
      <cards-in-hand-actions-mobile v-else-if="mode === 'mobile'" :can-act="canAct" :is-showdown="isShowdown"
        class="room__game-actions" />
    </transition>

    <edit-avatar-modal v-if="uiState.modals.editAvatar.isOpen" />

    <add-bot-modal v-if="isOwner && uiState.modals.addBot.isOpen" />

    <players-panel v-if="uiState.panels.playersPanel.isOpen" :editable="isOwner"></players-panel>

    <session-log-panel v-if="uiState.panels.sessionLog.isOpen" />

    <!-- TODO: For now it's on separate page /join/:id. Use it if socket gets watch event-->
    <!-- v-else-if="uiState.modals.createGame.isOpen" -->
    <!-- @vue:unmounted="activateSeat" -->
    <!-- <join-game-modal
        v-if="uiState.modals.createGame.isOpen"
        title="New Game"
        welcome-text="Play free online poker with your friends."
        action-text="Create Game"
      /> -->

    <join-game-modal v-if="uiState.modals.joinGame.isOpen" title="Join Table"
      welcome-text="You've been invited to play free online poker." action-text="Join Game" />
  </div>
</template>

<style scoped>
.compete-room {
  position: relative;

  width: 100vw;
  height: 100dvh;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.compete-room--desktop {
  background-image: url("@/assets/nlh-compete-room.jpg") !important;
}

.compete-room--mobile {
  background-image: url("@/assets/nlh-compete-room-mobile.jpg") !important;
}

.compete-room__table {
  z-index: 1;
}
</style>
